// Single source of truth for cross-platform design tokens.
// Both apps/web/tailwind.config.ts and apps/mobile/lib/colors.ts import from here.
// Any hex that exists in only one platform's config is a rule violation (divergence audit).
//
// K5 (May 2026): Brand primary = #F5E642 chalk yellow. Native was correct; PWA swept here.
// Sport gradient colors (basketball amber #F59E0B etc.) are NOT brand primary — do not change.

// ─── Phase B1 flat exports — consumed by tailwind.config.ts (chalk namespace) and colors.ts ───
// DO NOT add colors, spacing, or motion values anywhere else without adding them here first.

export const shell = {
  bg:        '#0B0F1A',  // app shell, navigation chrome
  card:      '#131929',  // event cards, roster rows
  elevated:  '#1C2438',  // modals, bottom sheets
  execution: '#07090F',  // Game Day, Field Mode (near-OLED black)
} as const

export const brand = {
  primary:   '#F5E642',  // chalk yellow — NEVER use as text on light backgrounds (contrast 1.33:1)
  navy:      '#0f172a',  // dark navy
  fieldMode: '#07090F',  // field mode + game day execution surface
} as const

export const darkText = {
  primary:   'rgba(255,255,255,1.0)',   // 18.1:1 on navy — headlines, primary content
  secondary: 'rgba(255,255,255,0.65)',  // 7.1:1 — labels, secondary content
  tertiary:  'rgba(255,255,255,0.50)',  // 5.4:1 — captions, metadata, hint text
  // BANNED: rgba(255,255,255,0.30) = 3.8:1 — fails WCAG 1.4.3 AA for normal-sized text
} as const

export const lightText = {
  primary:   '#1A1814',  // warm near-black — practice plan library
  secondary: '#3D3B35',  // warm dark gray
  // Sprint 61.14 — bumped from #7A7670 (4.32:1) to #6C6961 (5.00:1 on white,
  // 4.79:1 on warm-white #FAFAF8). The prior value failed WCAG 2.2 AA SC 1.4.3
  // for body text. Aligned with the team's existing AA-passing `text3Muted`
  // variant below — same source of truth, no duplicate values.
  tertiary:  '#6C6961',  // tags, metadata, captions
  action:    '#B45309',  // amber-700 — active nav labels on light backgrounds (4.7:1 on white)
  // NOTE: #F5E642 on white = 1.33:1 — NEVER use brand.primary as text on light backgrounds
} as const

export const motion = {
  ruleSweep:      280,  // ms — yellow rule entry (ease-out)
  springEntry:    260,  // ms — card/panel spring drop (slight overshoot)
  completePulse:  340,  // ms — drill completion, RSVP confirm, score increment
} as const

export const stripe = {
  height: 2,           // px — 2px chalk yellow horizontal rule (confirmed-state signal)
  color:  '#F5E642',
} as const

export const tapTarget = {
  app:       44,  // px minimum — all interactive elements app-wide
  fieldMode: 60,  // px minimum — drill step rows in Field Mode
} as const

export const warmPalette = {
  bgPage:   '#FAFAF8',  // warm near-white page background (practice plan library)
  bgPanel:  '#FFFFFF',  // card/panel backgrounds
  bgSunken: '#F5F4F0',  // inputs, collapsed sections
  border:   '#E8E6E0',  // warm card borders
} as const

// ── Trainer persona tokens ────────────────────────────────────────────────────
// Sprint 61.48 (2026-05-14): REVERSED Path A. Trainer surfaces share the
// canonical Chalk yellow + navy + slate system with coach/team/org. Path A's
// brand-per-product violet (#8B5CF6) created two competing visual identities
// — a screenshot of any trainer surface stopped reading as "Chalk." Every
// trainer surface now uses the same chrome and CTA color as coach.
//
// The `trainer` export is kept as a back-compat alias so import sites resolve
// to the unified system without an across-the-board refactor. New trainer code
// should prefer importing from `chalkBrand` / Tailwind tokens directly:
//   - Primary CTAs:    chalkBrand.primary  (#F5E642)
//   - CTA text/icon:   chalkBrand.navy     (#0F172A)
//   - Chrome / nav:    chalkBrand.navy
//   - Page bg:         #f8fafc (slate-50)
//   - Panel bg:        #FFFFFF
//   - Borders:         #E2E8F0 (slate-200)
//
// Path A's original audit doc is preserved for historical comparison at
// chalk-os/audits/design/2026-05-13-trainer-flow-competitive-benchmark.md.

export const chalkBrand = {
  primary:          '#F5E642',                       // canonical chalk yellow
  primaryHover:     '#E5D632',                       // hover/active
  primaryDark:      '#0F172A',                       // navy — AA text on yellow OR yellow text on navy
  navy:             '#0F172A',                       // chrome / nav / dark surfaces
  slate50:          '#F8FAFC',                       // page bg
  slate200:         '#E2E8F0',                       // borders
  slate600:         '#475569',                       // secondary text on light bg
  slate900:         '#0F172A',                       // primary text on light bg (same as navy)
} as const

// ── Chalkboard — marketing/identity dark chrome (Direction A, 2026-05-28) ──────
// A literal chalkboard (deep warm green-black), NOT navy. Consolidates the four
// ad-hoc marketing darks (#0f172a navy, #071428 chalk-dark, #0C0E14, #080E1A)
// into one cohesive surface so prospects see a single designed palette.
// Brand yellow #F5E642 is UNCHANGED and reserved for the single CTA per surface.
// IN-APP navy chrome (sidebar, cards) is intentionally unchanged here — that
// migration is Phase 3. Owner-approved palette direction (sign-off 2026-05-28).
export const chalkboard = {
  bg:       '#13201B',  // base green-black
  bgDeep:   '#0F1813',  // gradient end / deepest panel
  bgRaised: '#1B2A23',  // gradient start / raised dark panel
  gradient: 'linear-gradient(165deg, #1B2A23 0%, #13201B 55%, #0F1813 100%)',
  text:     '#F4F2EC',  // chalk-white primary text (≈15:1 on bg)
  muted:    '#9FB0A6',  // green-tinted secondary text (AA on bg)
  faint:    '#7E8F84',  // green-tinted tertiary / caption
  cta:      '#0F5132',  // evergreen CTA on light surfaces (AA on warm-white)
} as const

// Back-compat alias for trainer surfaces. Maps the old `trainer.*` shape to
// the unified Chalk palette so import sites need zero changes for files that
// used token imports (only files with inline #7C3AED / #A78BFA hex literals
// need an explicit sweep).
export const trainer = {
  primary:          chalkBrand.primary,              // was violet-500 — now chalk yellow
  primaryHover:     chalkBrand.primaryHover,         // was violet-600 — now yellow hover
  primaryDark:      chalkBrand.primaryDark,          // was violet-700 (AA text on light) — now navy (AA text on yellow + AA text on light)
  tint:             'rgba(245,230,66,0.10)',         // yellow-tinted subtle bg
  tintStrong:       'rgba(245,230,66,0.22)',         // stronger yellow tint
  tintBorder:       'rgba(245,230,66,0.35)',         // yellow border on tint
  tintBorderStrong: 'rgba(245,230,66,0.55)',         // yellow border on tintStrong
  focusRing:        'rgba(15,23,42,0.20)',           // navy halo for focus ring (AA on light)
  focusBorder:      '#0F172A',                       // navy 2px focus outline
  chip: {
    bg:        'rgba(245,230,66,0.18)',              // yellow chip bg on light
    border:    'rgba(15,23,42,0.20)',                // navy chip border
    text:      '#F5E642',                            // chip text on dark navy surfaces (chalk yellow)
    textLight: '#0F172A',                            // chip text on warm-light surfaces (navy — AA-pass)
  },
} as const

// Back-compat alias — `apps/mobile/lib/colors.ts` imports `trainerCard.accent`.
// Routes through `trainer.primary` so consumers see the same color. Do not
// remove until mobile colors.ts is migrated to import `trainer` directly.
export const trainerCard = {
  accent: trainer.primary,
} as const

export const warningBanner = {
  bg:     '#FFFBEB',  // amber-50 — Stripe-incomplete / setup banner background
  fg:     '#92400E',  // amber-900 — banner foreground text
  border: '#FDE68A',  // amber-200 — banner border
} as const

export const moneyBalance = {
  positive: '#15803D',  // green-700 — earned-revenue balance on Money tab
} as const

// One palette per TrainingSessionStatus value (see packages/shared/src/trainer/types.ts).
// Each entry provides bg/fg/border for status pill rendering.
export const sessionStatus = {
  booked: {
    bg:     '#FFFBEB',  // amber-50 — needs trainer confirmation (Sprint 55 surface)
    fg:     '#92400E',  // amber-900
    border: '#FDE68A',  // amber-200
  },
  confirmed: {
    bg:     '#ECFDF5',  // emerald-50 — active, trainer-confirmed
    fg:     '#065F46',  // emerald-800
    border: '#A7F3D0',  // emerald-200
  },
  checked_in: {
    bg:     '#EFF6FF',  // blue-50 — in-session
    fg:     '#1E40AF',  // blue-800
    border: '#BFDBFE',  // blue-200
  },
  completed: {
    bg:     '#F1F5F9',  // slate-100 — terminal success, muted
    fg:     '#475569',  // slate-600
    border: '#E2E8F0',  // slate-200
  },
  no_show: {
    bg:     '#FFF1F2',  // rose-50 — warning state, costed
    fg:     '#9F1239',  // rose-800
    border: '#FECDD3',  // rose-200
  },
  cancelled_by_guardian: {
    bg:     '#F8FAFC',  // slate-50 — quiet terminal, no fault
    fg:     '#64748B',  // slate-500
    border: '#E2E8F0',  // slate-200
  },
  cancelled_by_trainer: {
    bg:     '#F8FAFC',  // slate-50 — quiet terminal, trainer-initiated
    fg:     '#64748B',  // slate-500
    border: '#E2E8F0',  // slate-200
  },
} as const

export const tokens = {
  colors: {
    // ── Brand ─────────────────────────────────────────────────────────────
    brandPrimary: '#F5E642',      // Chalk yellow — K5 canonical value
    brandPrimaryHover: '#E8D44D', // Hover / active ring state (P2 bracket sweep)
    brandPrimaryDark: '#E5D632',  // Pressed state (5% darker)
    brandPrimaryTint: '#FEFCE8',  // Pale tint for badge backgrounds (yellow-50)
    brandPrimaryDimDark: 'rgba(245,230,66,0.12)',  // Brand yellow at 12% opacity — icon bg on dark surfaces

    // ── Warm palette (Phase 0.5 design strategy — Playbook / library surfaces) ──
    bgApp: '#FAFAF8',    // Warm near-white app background
    bgCard: '#FFFFFF',
    bgReceipt: '#FFF9F0', // Trust-warm payments (K12 design)
    bgSunken: '#F5F4F0', // Inputs, collapsed sections

    border: '#E8E6E0',   // Warm card border

    text1: '#1A1814',    // Primary text
    text2: '#3D3B35',    // Secondary / body text
    // Sprint 61.14 — text3 promoted to the AA-passing value (5.00:1 on white,
    // 4.79:1 on warm-white). The prior #7A7670 failed SC 1.4.3 at 4.32:1.
    // text3Muted alias retained for back-compat with v2 consumers; both
    // resolve to the same value going forward.
    text3:      '#6C6961', // Tertiary / tags / metadata — AA-passing on warm-white
    text3Muted: '#6C6961', // Alias kept for v2 mobile consumers — same value

    // ── Dark surfaces ──────────────────────────────────────────────────────
    darkSurface: '#07090F',  // Game Day execution surface — unchanged (Warm Functional)
    darkNavy: '#0f172a',     // Header / tab bar

    // ── Chalkboard (marketing/identity dark — Direction A) ───────────────────
    chalkboard,              // see export above; marketing dark chrome (not navy)

    // ── Actions ────────────────────────────────────────────────────────────
    actionCTA: '#0066FF', // Completion / onboarding CTAs (blue, not brand yellow)

    // ── Status ─────────────────────────────────────────────────────────────
    success:     '#22c55e',
    successMuted: '#107A55', // AA-passing success for text use (5.04:1 on white, 4.82:1 on warm-white)
    successTint: '#F0FDF4',  // green-50 — badge backgrounds, success state tints
    error:       '#ef4444',
    // #EF4444 is 3.4:1 on warm-white — FAILS WCAG 1.4.3 for body text.
    // errorMuted is the AA-passing variant (5.96:1 on white, ~5.7:1 on
    // #FAFAF8) — mirrors successMuted/warningMuted. Home v2 + Auth route
    // Typography tone="error" through this token (#215, Home-v2 a11y gate).
    errorMuted:  '#B91C1C',  // AA-passing error for body text (5.96:1 on white) — use when error renders on light surface
    warning:     '#D97706',
    warningMuted: '#B45309', // AA-passing warning for text use (5.02:1 on white)
    warningTint: '#FFFBEB',  // amber-50 — warning badge backgrounds

    // ── Sport identity gradients ────────────────────────────────────────────
    // These are NOT brand primary. Do not change to #F5E642.
    sport: {
      basketball: '#F59E0B', // Amber 500
      baseball:   '#FB923C', // Orange 400
      soccer:     '#16A34A', // Green 600
      lacrosse:   '#7C3AED', // Violet 600
      hockey:     '#0891B2', // Cyan 600
      volleyball: '#DC2626', // Red 600
      flagFootball: '#CA8A04', // Yellow 600
      softball:   '#FBBF24', // Amber 400
    },

    // ── Tier badge colors ──────────────────────────────────────────────────
    orgTier: '#a855f7', // Purple — Chalk Org subscription badge (distinct from Coach yellow)

    // ── Role badge colors (DI-5/DP-3 — shared across PWA and native) ──────────
    roleColors: {
      coach:     '#F5E642',
      parent:    '#60a5fa',
      treasurer: '#34d399',
      org_admin: '#f472b6',
      fan:       '#a78bfa',
      director:  '#fb923c',
    },

    // ── Drill type identity (Pattern E — Phase 0.5) ─────────────────────────
    drillType: {
      warmup:      '#FCD34D',
      skill:       '#60A5FA',
      competition: '#34D399',
      cooldown:    '#9CA3AF',
    },

    // ── Dark palette (Phase 5 prep — tokens defined, not yet applied) ────────
    // Full dark mode requires useColorScheme + conditional styling in screens.
    // app.json userInterfaceStyle stays 'light' until implementation is complete.
    dark: {
      bgApp:    '#0D0D12', // Warm near-black page background
      bgCard:   '#17171E', // Card/panel surface
      bgSunken: '#0A0A0F', // Inputs, collapsed sections
      border:   '#2A2A35', // Card border
      text1:    '#F0EEE8', // Primary text
      text2:    '#B8B5AE', // Secondary / body text
      text3:    '#7A7670', // Tertiary / metadata — 4.51:1 on dark bg #0D0D12 (AA-passing on dark; on light surfaces use the lightSurface.text3 variant which is now #6C6961)
    },

    // ── Neutral dark palette (shared between game day surfaces and nav) ──────
    slate800: '#1e293b',  // Tailwind slate-800 — undo snackbars, dark overlays

    // ── GameDay execution surface ──────────────────────────────────────────
    gameDay: {
      subSurface:   '#111111',  // panels inside GameDay (inning grid, count, stats table)
      atBat:        '#1a1a2e',  // current at-bat card, stats header rows
      border:       '#222222',  // dark dividers and card borders
      handle:       '#374151',  // modal drag handles, inactive BSO dots
      text:         '#ffffff',  // primary text on dark surfaces
      textMuted:    '#6b7280',  // secondary / label text (cool-gray 500)
      textLight:    '#e5e7eb',  // stat values in grids (cool-gray 200)
      textTertiary: '#9ca3af',  // very dim / AVG text (cool-gray 400)
      textDim:      '#4b5563',  // inactive stat values (cool-gray 600)
      rowAlt:       '#0d0d0d',  // alternating stats row background
      chatBubble:   '#1e1e1e',  // incoming chat bubble background
      // Baseball pitch-outcome button surfaces (dark game-day context only)
      foulBg:       '#431407',  // foul button background (deep amber-950)
      foulAccent:   '#f97316',  // foul button text/border (orange-500)
      inPlayBg:     '#1e3a5f',  // in-play button background (deep blue-900)
      inPlayAccent: '#60a5fa',  // in-play button text/border (blue-400)
      mound:        '#4a3520',  // pitcher's mound dirt color (game field diagram)
    },

    // ── Field diagram ──────────────────────────────────────────────────────
    field: {
      grass:       '#0d1f0a',  // outfield fill, main infield base
      grassMid:    '#2d4a1e',  // outfield arc border, infield square fill
      grassBorder: '#3d6a2e',  // infield square border
    },
  },

  typography: {
    serif: 'Lora',  // Editorial-warm context only (Playbook / practice plan library)
    sans:  'Inter', // Functional-warm everywhere else
    weights: {
      regular:  400,
      medium:   500,
      semibold: 600,
      bold:     700,
    },
  },

  spacing: {
    base: 8, // 8px grid
  },

  radius: {
    card:   16,   // rounded-2xl
    pill:   9999, // rounded-full
    button: 12,   // rounded-xl
  },

  // #230 — drill-search.tsx (and any v2 consumer) reads `tokens.tapTarget.app`.
  // tapTarget was a standalone export but absent from this aggregate, so
  // `tokens.tapTarget` was `undefined` → `.app` is a runtime TypeError on the
  // (now v2-live, #227) drill-search screen. Expose the existing const here;
  // 44px app-wide / 60px field mode is the canonical a11y minimum.
  tapTarget,
} as const

export type Tokens = typeof tokens
